import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Plus,
  Trash2,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Link,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const ToggleSwitch = ({ enabled, onToggle }) => {
  const Icon = enabled ? ToggleRight : ToggleLeft;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center space-x-1.5 group"
    >
      <Icon
        className={`w-6 h-6 transition-colors ${
          enabled
            ? "text-green-500 group-hover:text-green-600"
            : "text-slate-300 group-hover:text-slate-400"
        }`}
      />
      <span className="text-xs text-slate-500">
        {enabled ? "Active" : "Paused"}
      </span>
    </button>
  );
};

const CalendarIntegration = () => {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ display_name: "", feed_url: "" });
  const [adding, setAdding] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const flash = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess(null);
    } else {
      setSuccess(msg);
      setError(null);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 4000);
  };

  // Load facilities once
  useEffect(() => {
    fetch(`${AUTH_URL}/facilities`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFacilities(list);
        if (list.length > 0) setFacilityId(list[0].facility_id || "");
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFeeds = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/fieldiq/ics-feeds?facility_id=${encodeURIComponent(facilityId)}`,
        { headers },
      );
      const data = await res.json();
      if (res.ok) {
        setFeeds(Array.isArray(data) ? data : []);
      } else {
        flash(data.error || "Failed to load feeds", true);
      }
    } catch {
      flash("Network error loading feeds", true);
    }
    setLoading(false);
  }, [facilityId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.feed_url.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${AUTH_URL}/fieldiq/ics-feeds`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          display_name: form.display_name.trim() || "Calendar",
          feed_url: form.feed_url.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const synced = data.sync?.synced ?? 0;
        flash(
          `Feed added and synced ${synced} event${synced !== 1 ? "s" : ""}.`,
        );
        setForm({ display_name: "", feed_url: "" });
        loadFeeds();
      } else {
        flash(data.error || "Failed to add feed", true);
      }
    } catch {
      flash("Network error adding feed", true);
    }
    setAdding(false);
  };

  const handleSync = async (feedId) => {
    setSyncing(feedId);
    try {
      const res = await fetch(
        `${AUTH_URL}/fieldiq/ics-feeds/${feedId}/sync?facility_id=${encodeURIComponent(facilityId)}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ facility_id: facilityId }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        flash(
          `Synced ${data.synced ?? 0} event${(data.synced ?? 0) !== 1 ? "s" : ""}${data.errors ? ` (${data.errors} error${data.errors !== 1 ? "s" : ""})` : ""}.`,
        );
        loadFeeds();
      } else {
        flash(data.error || "Sync failed", true);
      }
    } catch {
      flash("Network error during sync", true);
    }
    setSyncing(null);
  };

  const handleToggle = async (feed) => {
    try {
      const res = await fetch(`${AUTH_URL}/fieldiq/ics-feeds/${feed.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          sync_enabled: !feed.sync_enabled,
        }),
      });
      if (res.ok) {
        setFeeds((prev) =>
          prev.map((f) =>
            f.id === feed.id ? { ...f, sync_enabled: !f.sync_enabled } : f,
          ),
        );
      }
    } catch {}
  };

  const handleDelete = async (feed) => {
    if (
      !window.confirm(
        `Delete "${feed.display_name}" and all its synced events?`,
      )
    )
      return;
    try {
      const res = await fetch(
        `${AUTH_URL}/fieldiq/ics-feeds/${feed.id}?facility_id=${encodeURIComponent(facilityId)}`,
        { method: "DELETE", headers },
      );
      if (res.ok) {
        flash("Feed and synced events deleted.");
        setFeeds((prev) => prev.filter((f) => f.id !== feed.id));
      } else {
        const data = await res.json();
        flash(data.error || "Delete failed", true);
      }
    } catch {
      flash("Network error", true);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "Never";
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">
          Calendar Integration
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect public .ics feed URLs so FieldIQ automatically syncs events
          every 15 minutes.
        </p>
      </div>

      {/* Facility selector */}
      {facilities.length > 1 && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Facility
          </label>
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Flash messages */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Add feed form */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Calendar Feed
        </h3>
        <form onSubmit={handleAdd} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, display_name: e.target.value }))
              }
              placeholder="e.g. LD4 District Calendar"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              .ics Feed URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              required
              value={form.feed_url}
              onChange={(e) =>
                setForm((f) => ({ ...f, feed_url: e.target.value }))
              }
              placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">
              Google Calendar: Settings → share → "Secret address in iCal
              format"
            </p>
          </div>
          <button
            type="submit"
            disabled={adding || !facilityId}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {adding ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {adding ? "Adding & syncing…" : "Add Feed"}
          </button>
        </form>
      </div>

      {/* Feed list */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm divide-y divide-slate-100">
        <div className="px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Connected Feeds
            {feeds.length > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-400">
                ({feeds.length})
              </span>
            )}
          </h3>
          <button
            onClick={loadFeeds}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading && feeds.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            Loading…
          </div>
        ) : feeds.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            No feeds connected yet. Add one above.
          </div>
        ) : (
          feeds.map((feed) => (
            <div key={feed.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {feed.display_name}
                    </p>
                    <a
                      href={feed.feed_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-400 hover:text-blue-500 flex items-center gap-1 mt-0.5 truncate"
                    >
                      <Link className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{feed.feed_url}</span>
                    </a>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-400">
                        Last sync: {fmtDate(feed.last_synced_at)}
                      </span>
                    </div>
                    {feed.last_error && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                        {feed.last_error}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ToggleSwitch
                    enabled={feed.sync_enabled}
                    onToggle={() => handleToggle(feed)}
                  />
                  <button
                    onClick={() => handleSync(feed.id)}
                    disabled={syncing === feed.id}
                    title="Sync now"
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${syncing === feed.id ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    onClick={() => handleDelete(feed)}
                    title="Delete feed"
                    className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* How-to guide */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-sm text-slate-600 space-y-2">
        <p className="font-semibold text-slate-700">Getting your .ics URL</p>
        <ul className="space-y-1 list-disc list-inside text-slate-500">
          <li>
            <strong>Google Calendar:</strong> Settings → calendar → "Integrate
            calendar" → copy "Secret address in iCal format"
          </li>
          <li>
            <strong>Apple Calendar (iCloud):</strong> icloud.com → Calendar →
            share icon → Public Calendar → copy link
          </li>
          <li>
            <strong>Outlook:</strong> Calendar settings → Shared calendars →
            Publish → copy ICS link
          </li>
          <li>
            <strong>Cal.com / Calendly:</strong> Copy the .ics export URL from
            your calendar settings
          </li>
        </ul>
        <p className="text-slate-400 text-xs">
          Events sync every 15 minutes. Only events with CONFIRMED or TENTATIVE
          status appear in FieldIQ.
        </p>
      </div>
    </div>
  );
};

export default CalendarIntegration;
